<button class="relative overflow-visible" mat-icon-button (click)="openPanel()" #notificationsOrigin>
    <mat-icon class="relative z-0" [svgIcon]="'heroicons_outline:bell-alert'"></mat-icon>
    @if (displayUnreadCount > 0) {
    <span class="pointer-events-none absolute -top-0.5 left-0 right-0 z-20 flex h-4 items-center justify-center">
        <span
            class="ml-4 flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-primary px-1 text-xs font-medium leading-none text-white">
            {{ displayUnreadCount > 99 ? '99+' : displayUnreadCount }}
        </span>
    </span>
    }
</button>

<!-- Live notification cards (appear when a new notification arrives) -->
@if (toastNotifications.length) {
<div class="pointer-events-none fixed right-3 top-16 z-9999 flex w-[min(100vw-1.5rem,24rem)] flex-col gap-2.5 sm:right-5">
    @for (toast of toastNotifications; track toast.id) {
    <div
        class="notification-live-card pointer-events-auto relative flex cursor-pointer items-start gap-3 overflow-hidden rounded-xl border-2 border-primary bg-card px-3.5 pb-4 pt-3 shadow-lg"
        (click)="onToastClick(toast)"
        (mouseenter)="pauseToast(toast.id)"
        (mouseleave)="resumeToast(toast.id)">
        <div
            class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary dark:bg-primary-500/25 dark:text-primary-300">
            <mat-icon class="icon-size-5 text-current"
                [svgIcon]="toast.icon || 'heroicons_outline:bell'"></mat-icon>
        </div>

        <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2">
                <span class="truncate text-sm font-semibold leading-5 text-gray-900 dark:text-white">
                    {{ toast.title }}
                </span>
                <span class="h-2 w-2 shrink-0 rounded-full bg-primary"></span>
            </div>

            @if (toast.description) {
            <p class="mt-0.5 line-clamp-2 text-sm leading-5 text-gray-500 dark:text-gray-400">
                {{ toast.description }}
            </p>
            }

            <div class="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {{ toast.time | date: 'MMM d, h:mm a' }}
            </div>
        </div>

        <button type="button"
            class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-400 transition hover:bg-black/5 hover:text-gray-600 dark:hover:bg-white/10"
            (click)="dismissToast(toast.id, $event)" [matTooltip]="'Dismiss'">
            <mat-icon class="icon-size-4" [svgIcon]="'heroicons_mini:x-mark'"></mat-icon>
        </button>

        <div class="notification-toast-timer absolute inset-x-0 bottom-0 h-1 origin-left bg-primary"
            aria-hidden="true"></div>
    </div>
    }
</div>
}

<ng-template #notificationsPanel>
    <div
        class="fixed inset-0 flex flex-col overflow-hidden bg-card shadow-lg sm:static sm:inset-auto sm:w-140 sm:min-w-140 sm:rounded-2xl">
        <!-- Header -->
        <div class="flex shrink-0 items-center bg-primary px-5 py-4 text-on-primary">
            <div class="-ml-1 mr-2 sm:hidden">
                <button mat-icon-button (click)="closePanel()">
                    <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_solid:x-mark'"></mat-icon>
                </button>
            </div>
            <div class="text-lg font-semibold">Notifications</div>
            <div class="ml-auto">
                <button mat-icon-button [matTooltip]="'Clear all'" [disabled]="!notifications.length"
                    (click)="clearAll()">
                    <mat-icon class="text-current icon-size-5" [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                </button>
            </div>
        </div>

        <!-- Filter tabs -->
        <div class="shrink-0 px-4 py-3">
            <div class="flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
                <button type="button"
                    class="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition"
                    [ngClass]="activeFilter === 'all'
                        ? 'bg-card text-gray-900 shadow-sm dark:text-white'
                        : 'text-secondary hover:text-gray-900 dark:hover:text-white'"
                    (click)="setFilter('all')">
                    <span>All</span>
                    <span
                        class="rounded-md bg-gray-200/90 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {{ allCount }}
                    </span>
                </button>
                <button type="button"
                    class="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition"
                    [ngClass]="activeFilter === 'unread'
                        ? 'bg-card text-gray-900 shadow-sm dark:text-white'
                        : 'text-secondary hover:text-gray-900 dark:hover:text-white'"
                    (click)="setFilter('unread')">
                    <span>Unread</span>
                    <span
                        class="rounded-md bg-gray-200/90 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {{ displayUnreadCount }}
                    </span>
                </button>
                <button type="button"
                    class="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2.5 py-2 text-sm font-medium transition"
                    [ngClass]="activeFilter === 'read'
                        ? 'bg-card text-gray-900 shadow-sm dark:text-white'
                        : 'text-secondary hover:text-gray-900 dark:hover:text-white'"
                    (click)="setFilter('read')">
                    <span>Read</span>
                    <span
                        class="rounded-md bg-gray-200/90 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        {{ displayReadCount }}
                    </span>
                </button>
            </div>
        </div>

        <!-- Content -->
        <div class="relative flex min-h-0 flex-auto flex-col overflow-y-auto px-4 pb-4 pt-1 sm:max-h-120">
            @if (loading) {
            <div class="flex flex-auto items-center justify-center py-12">
                <mat-spinner diameter="32"></mat-spinner>
            </div>
            } @else {
            @for (notification of filteredNotifications; track notification.id) {
            <div class="group relative mb-2.5 shrink-0 cursor-pointer overflow-hidden rounded-xl border transition"
                [ngClass]="!notification.read
                    ? 'border-primary-200 bg-primary-50 shadow-[0_2px_10px_rgba(var(--fuse-primary-rgb,1,126,132),0.18)] dark:border-primary-500/35 dark:bg-primary-500/10 dark:shadow-[0_2px_10px_rgba(var(--fuse-primary-rgb,1,126,132),0.22)]'
                    : 'border-gray-200 bg-card shadow-sm dark:border-gray-700 dark:bg-gray-900/40'"
                (click)="openNotification(notification)">
                @if (!notification.read) {
                <span class="absolute inset-y-0 left-0 w-1 rounded-l-xl bg-primary"></span>
                }

                <div class="flex items-start gap-3 px-3.5 py-3"
                    [ngClass]="!notification.read ? 'pl-4' : ''">
                    <!-- Icon -->
                    <div
                        class="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                        <mat-icon class="icon-size-5 text-current"
                            [svgIcon]="notification.icon || 'heroicons_outline:bell'"></mat-icon>
                    </div>

                    <!-- Body -->
                    <div class="min-w-0 flex-1">
                        <div class="flex items-start gap-2">
                            <div class="min-w-0 flex-1">
                                <div class="truncate text-sm font-semibold leading-5 text-gray-900 dark:text-white">
                                    {{ notification.title }}
                                </div>

                                @if (notification.description) {
                                <p class="mt-0.5 truncate text-sm leading-5 text-gray-500 dark:text-gray-400">
                                    {{ notification.description }}
                                </p>
                                }
                            </div>

                            <div class="flex shrink-0 items-center gap-0.5 text-gray-400">
                                <button type="button"
                                    class="flex h-7 w-7 items-center justify-center rounded-md transition hover:bg-black/5 hover:text-gray-600 dark:hover:bg-white/10"
                                    (click)="toggleExpand(notification, $event)"
                                    [matTooltip]="isExpanded(notification) ? 'Collapse' : 'Expand'">
                                    <mat-icon class="icon-size-4 transition-transform"
                                        [ngClass]="isExpanded(notification) ? 'rotate-180' : ''"
                                        [svgIcon]="'heroicons_mini:chevron-down'"></mat-icon>
                                </button>
                                <button type="button"
                                    class="flex h-7 w-7 items-center justify-center rounded-md transition hover:bg-black/5 hover:text-gray-600 dark:hover:bg-white/10"
                                    (click)="dismiss(notification, $event)" [matTooltip]="'Dismiss'">
                                    <mat-icon class="icon-size-4"
                                        [svgIcon]="'heroicons_mini:x-mark'"></mat-icon>
                                </button>
                            </div>
                        </div>

                        @if (isExpanded(notification)) {
                        <div class="mt-3 space-y-2.5">
                            @if (notification.detailSummary) {
                            <p class="text-sm leading-5 text-gray-600 dark:text-gray-300">
                                {{ notification.detailSummary }}
                            </p>
                            }

                            <div class="flex items-center justify-between gap-3">
                                <div class="text-xs text-gray-400 dark:text-gray-500">
                                    {{ notification.time | date: 'MMM d, h:mm a' }}
                                </div>
                                <button type="button"
                                    class="rounded-full border border-gray-300 bg-card px-4 py-1.5 text-sm font-medium text-gray-700 transition hover:border-primary hover:text-primary dark:border-gray-600 dark:text-gray-200 dark:hover:border-primary dark:hover:text-primary"
                                    (click)="openNotification(notification, $event)">
                                    @if (notification.link) { View } @else { Okay }
                                </button>
                            </div>
                        </div>
                        }
                    </div>
                </div>
            </div>
            }

            @if (!filteredNotifications.length) {
            <div class="flex flex-auto flex-col items-center justify-center px-8 py-12">
                <div
                    class="flex h-14 w-14 flex-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-600">
                    <mat-icon class="text-primary-700 dark:text-primary-50"
                        [svgIcon]="'heroicons_outline:bell'"></mat-icon>
                </div>
                <div class="mt-5 text-xl font-semibold tracking-tight text-gray-900 dark:text-white">
                    No notifications
                </div>
                <div class="mt-1 max-w-60 text-center text-sm text-gray-500 dark:text-gray-400">
                    @if (activeFilter === 'unread') {
                    You're all caught up. No unread notifications.
                    } @else if (activeFilter === 'read') {
                    No read notifications yet.
                    } @else {
                    When you receive notifications, they will appear here.
                    }
                </div>
            </div>
            }
            }
        </div>

        <!-- Footer -->
        <div class="shrink-0 border-t px-5 py-3 dark:border-gray-700">
            <button type="button"
                class="text-primary text-sm font-medium underline disabled:cursor-not-allowed disabled:opacity-50"
                [disabled]="displayUnreadCount === 0" (click)="markAllAsRead()">
                Mark all as read
            </button>
        </div>
    </div>
</ng-template>
